import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCommandPaletteOpen } from '../../../redux/slices/commandPaletteSlice';

function shouldIgnoreShortcut(target) {
  if (!target) return false;

  const tagName = target.tagName?.toLowerCase();
  if (tagName === 'textarea' || tagName === 'select') return true;
  if (target.isContentEditable) return true;
  if (target.closest?.('[contenteditable="true"]')) return true;
  if (target.closest?.('.guideline-editor, .lexical-editor, [data-lexical-editor]')) return true;

  if (tagName === 'input') {
    const inputType = target.type?.toLowerCase();
    if (
      inputType === 'text' ||
      inputType === 'search' ||
      inputType === 'email' ||
      inputType === 'password' ||
      inputType === 'url' ||
      !inputType
    ) {
      return true;
    }
  }

  return false;
}

function useCommandPalette() {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.commandPalette.isOpen);

  const open = useCallback(() => {
    dispatch(setCommandPaletteOpen(true));
  }, [dispatch]);

  const close = useCallback(() => {
    dispatch(setCommandPaletteOpen(false));
  }, [dispatch]);

  const toggle = useCallback(() => {
    dispatch(setCommandPaletteOpen(!isOpen));
  }, [dispatch, isOpen]);

  useEffect(() => {
    const handleKeyDown = event => {
      const isModKey = event.metaKey || event.ctrlKey;

      if (isModKey && event.key.toLowerCase() === 'k') {
        if (!isOpen && shouldIgnoreShortcut(event.target)) return;
        event.preventDefault();
        toggle();
        return;
      }

      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        close();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [close, isOpen, toggle]);

  return { isOpen, open, close };
}

export default useCommandPalette;
